import { FileSearch } from "lucide-react";
import { ButtonLink, EmptyState } from "@/components/system";
import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { BarList } from "@/components/access-report/BarList";
import { CiteBlock } from "@/components/access-report/CiteBlock";
import { ReportCtas } from "@/components/access-report/ReportCtas";
import { SpecialtyTable } from "@/components/access-report/SpecialtyTable";
import { StatBand } from "@/components/access-report/StatBand";
import { WaitReports } from "@/components/access-report/WaitReports";
import { PublicShell } from "@/components/layout/PublicShell";
import { useCity } from "@/contexts/CityContext";
import {
  formatReportDate,
  useAccessReportIssue,
  type AccessReportIssue as AccessReportIssuePayload,
  type AdminAccessReportIssue,
} from "@/lib/access-report";
import { track } from "@/lib/analytics";
import { SEO, accessReportSeoMetadata } from "@/lib/seo";
import { buildAccessReportStructuredData } from "@/lib/structured-data";
import "@/styles/access-report.css";

export interface AccessReportIssueProps {
  issue?: AccessReportIssuePayload | AdminAccessReportIssue;
  preview?: boolean;
}

export default function AccessReportIssue({ issue, preview = false }: AccessReportIssueProps) {
  if (issue) return <AccessReportDocument issue={issue} preview={preview} />;
  return <AccessReportIssueRoute />;
}

function AccessReportIssueRoute() {
  const { city } = useCity();
  const { slug = "" } = useParams<{ slug: string }>();
  const report = useAccessReportIssue(city.slug, slug);
  const trackedSlug = useRef<string | null>(null);

  useEffect(() => {
    const issueSlug = report.data?.issue.slug;
    if (!issueSlug || trackedSlug.current === issueSlug) return;
    track("access_report_view", { slug: issueSlug });
    trackedSlug.current = issueSlug;
  }, [report.data?.issue.slug]);

  if (report.isLoading) {
    return (
      <PublicShell>
        <main className="pb-access-report-shell">
          <div className="pb-access-state" aria-live="polite">Loading the report snapshot…</div>
        </main>
      </PublicShell>
    );
  }

  if (report.isError || !report.data) {
    return (
      <PublicShell>
        <SEO
          title="Access Report not found · PulseBoard"
          description="This Specialist Access Report could not be found."
          path={`/access-report/${slug}`}
          noindex
        />
        <div className="ds-container ds-container--narrow">
          <div className="ds-section">
            <EmptyState icon={<FileSearch />} title="This report isn't available" action={<ButtonLink to="/access-report">All published reports</ButtonLink>}>
              It may not have been published yet, or the link may be out of date.
            </EmptyState>
          </div>
        </div>
      </PublicShell>
    );
  }

  const issue = report.data.issue;
  const metadata = accessReportSeoMetadata(issue);
  return (
    <PublicShell>
      <SEO
        title={metadata.title}
        description={metadata.description}
        path={metadata.path}
        publishedTime={issue.publishedAt}
        jsonLd={buildAccessReportStructuredData(issue)}
      />
      <div className="ds-container">
        <AccessReportDocument issue={issue} />
      </div>
    </PublicShell>
  );
}

export function AccessReportDocument({
  issue,
  preview = false,
}: {
  issue: AccessReportIssuePayload | AdminAccessReportIssue;
  preview?: boolean;
}) {
  const findings = issue.narrative.keyFindings.filter((finding) => finding.trim())
    .concat(issue.narrative.keyFindings.some((finding) => finding.trim()) ? [] : issue.data.defaultFindings);
  const findability = issue.data.findability;
  const publishedLabel = issue.publishedAt
    ? `Published ${formatReportDate(issue.publishedAt)}`
    : "Draft preview — not published";

  return (
    <article className={`pb-access-report-shell${preview ? " pb-access-report-shell--preview" : ""}`}>
      {preview ? <p className="pb-access-preview-banner">Admin preview · Draft data may still be recomputed</p> : null}
      <header className="pb-access-masthead">
        <p className="pb-access-eyebrow">Quarterly specialist access dataset</p>
        <h1>{issue.title}</h1>
        <div className="pb-access-masthead__meta">
          <span>Issue No.{issue.issueNo}{issue.issueNo === 1 ? " — the baseline" : ""}</span>
          <span>{formatReportDate(issue.period.from)} – {formatReportDate(issue.period.to)}</span>
          <span>{publishedLabel}</span>
        </div>
        {issue.narrative.intro.trim() ? <p className="pb-access-deck">{issue.narrative.intro}</p> : null}
      </header>

      <StatBand headline={issue.data.headline} />

      <section className="pb-access-section pb-access-findings" aria-labelledby="access-findings-heading">
        <div className="pb-access-section-heading">
          <p className="pb-access-eyebrow">What the baseline says</p>
          <h2 id="access-findings-heading">Key findings</h2>
        </div>
        <ol>
          {findings.map((finding, index) => <li key={`${index}-${finding}`}>{finding}</li>)}
        </ol>
      </section>

      <section className="pb-access-section" aria-labelledby="access-findability-heading">
        <div className="pb-access-section-heading">
          <p className="pb-access-eyebrow">What a GP can find today</p>
          <h2 id="access-findability-heading">Findability</h2>
        </div>
        <BarList
          total={findability.total}
          items={[
            { label: "Website published", value: findability.website },
            { label: "Phone published", value: findability.phone },
            { label: "Email published", value: findability.email },
            { label: "Fax published", value: findability.fax },
            { label: "Referral method confirmed", value: findability.referralMethodConfirmed },
            { label: "Telehealth listed", value: findability.telehealthListed },
            { label: "Bulk billing listed", value: findability.bulkBillingListed },
          ]}
          valueLabel="listings"
        />
      </section>

      <section className="pb-access-section" aria-labelledby="access-specialty-heading">
        <div className="pb-access-section-heading">
          <p className="pb-access-eyebrow">Frozen snapshot</p>
          <h2 id="access-specialty-heading">By specialty</h2>
        </div>
        <SpecialtyTable bySpecialty={issue.data.bySpecialty} slug={issue.slug} />
      </section>

      <section className="pb-access-section" aria-labelledby="access-suburb-heading">
        <div className="pb-access-section-heading">
          <p className="pb-access-eyebrow">Directory distribution</p>
          <h2 id="access-suburb-heading">Where specialists are</h2>
        </div>
        <BarList
          total={issue.data.headline.listedSpecialists}
          items={issue.data.bySuburb.map((suburb) => ({ label: suburb.suburb, value: suburb.listed }))}
          valueLabel="specialists"
        />
      </section>

      <section className="pb-access-section" aria-labelledby="access-waits-heading">
        <div className="pb-access-section-heading">
          <p className="pb-access-eyebrow">GP-reported · suppression applies</p>
          <h2 id="access-waits-heading">Wait-time reports</h2>
        </div>
        <p className="pb-access-section-intro">Specialty-level results appear only after at least three verified reports in this issue period.</p>
        <WaitReports data={issue.data} />
      </section>

      <aside className="pb-access-context" aria-label="Directory context">
        <strong>Directory context</strong>
        <span>{issue.data.context.alliedHealthListed} allied health listings</span>
        <span>{issue.data.context.gpPracticesListed} GP practices</span>
        <span>{issue.data.context.briefSubscribers} brief subscribers</span>
        <span>{issue.data.context.members} members</span>
      </aside>

      <div className="pb-access-narrative-grid">
        <section className="pb-access-section" aria-labelledby="access-method-heading">
          <div className="pb-access-section-heading"><h2 id="access-method-heading">Methodology</h2></div>
          <p>{issue.narrative.methodology || "Methodology not yet supplied."}</p>
        </section>
        <section className="pb-access-section" aria-labelledby="access-notes-heading">
          <div className="pb-access-section-heading"><h2 id="access-notes-heading">Notes</h2></div>
          <p>{issue.narrative.notes || "No additional notes for this issue."}</p>
        </section>
      </div>

      <CiteBlock issue={issue} />
      <ReportCtas />
    </article>
  );
}
